import classNames from 'classnames';
import * as React from 'react';
import type { BaseSubjectProps, StudyModelLayoutProps } from '../StudyModelLayout';
import baseStyles from '../StudyModelLayout.module.css';
import verticalStyles from './StudyModelLayout.vertical.module.css';

const renderSubjects = <TSubjectProps extends BaseSubjectProps>(
    subjects: TSubjectProps[],
    renderSubject: StudyModelLayoutProps<TSubjectProps>['renderSubject']
) =>
    subjects.map(subject => (
        <div className={verticalStyles.Subject} key={subject.code}>
            {renderSubject(subject)}
        </div>
    ));

const renderEmptyMessage = (message: string) => (
    <span className={classNames(baseStyles.EmptyMessage, verticalStyles.EmptyMessage)}>{message}</span>
);

export const StudyModelVerticalLayout = <TSubjectProps extends BaseSubjectProps>(
    props: StudyModelLayoutProps<TSubjectProps>
) => {
    const { subjects, firstHeading, secondHeading, renderSubject, noSubjectsMessage } = props;

    const useSingleColumn = !secondHeading;

    if (useSingleColumn) {
        return (
            <StudyModelSingleColumnVerticalLayout
                firstHeading={firstHeading}
                subjects={subjects}
                renderSubject={renderSubject}
                noSubjectsMessage={noSubjectsMessage}
            />
        );
    }

    const firstSemesterSubjects = subjects.filter(subject => subject.semester === 'first');
    const secondSemesterSubjects = subjects.filter(subject => subject.semester === 'second');
    const bothSemestersSubjects = subjects.filter(subject => subject.semester === 'both');

    return (
        <div className={classNames(baseStyles.StudyModelLayout, verticalStyles.StudyModelLayout)}>
            <div className={classNames(baseStyles.SeparatorLine, verticalStyles.SeparatorLine)} />
            <div className={classNames(baseStyles.Heading, verticalStyles.Heading, verticalStyles['Heading--first'])}>
                {firstHeading}
            </div>
            <div className={classNames(baseStyles.Heading, verticalStyles.Heading, verticalStyles['Heading--second'])}>
                {secondHeading}
            </div>
            {subjects.length > 0 ? (
                <>
                    <span className={classNames(verticalStyles.Semester, verticalStyles['Semester--both'])}>
                        {renderSubjects(bothSemestersSubjects, renderSubject)}
                    </span>
                    <span className={classNames(verticalStyles.Semester, verticalStyles['Semester--first'])}>
                        {renderSubjects(firstSemesterSubjects, renderSubject)}
                    </span>
                    <span className={classNames(verticalStyles.Semester, verticalStyles['Semester--second'])}>
                        {renderSubjects(secondSemesterSubjects, renderSubject)}
                    </span>
                </>
            ) : (
                renderEmptyMessage(noSubjectsMessage)
            )}
            <div className={classNames(baseStyles.DashLine, verticalStyles.DashLine)} />
        </div>
    );
};

type StudyModelSingleColumnVerticalLayoutProps<TSubjectProps extends BaseSubjectProps> = Pick<
    StudyModelLayoutProps<TSubjectProps>,
    'firstHeading' | 'subjects' | 'renderSubject' | 'noSubjectsMessage'
>;

export const StudyModelSingleColumnVerticalLayout = <TSubjectProps extends BaseSubjectProps>(
    props: StudyModelSingleColumnVerticalLayoutProps<TSubjectProps>
) => {
    const { firstHeading, subjects, renderSubject, noSubjectsMessage } = props;

    return (
        <div
            className={classNames(
                baseStyles.StudyModelLayout,
                verticalStyles.StudyModelLayout,
                verticalStyles.SingleColumnLayout
            )}
        >
            <div className={classNames(baseStyles.Heading, verticalStyles.Heading, verticalStyles['Heading--first'])}>
                {firstHeading}
            </div>
            {subjects.length > 0 ? (
                <span
                    className={classNames(baseStyles.Heading, verticalStyles.Heading, verticalStyles['Heading--first'])}
                >
                    {renderSubjects(subjects, renderSubject)}
                </span>
            ) : (
                renderEmptyMessage(noSubjectsMessage)
            )}
        </div>
    );
};
